// HomePage.jsx – IMS dashboard with subscription blocking + real P&L
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiDollarSign,
  FiFileText,
  FiLogOut,
  FiPackage,
  FiRefreshCw,
  FiShoppingCart,
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
} from 'react-icons/fi';
import { Bar, Line } from 'react-chartjs-2';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../pages/CurrencyContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
);

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const getToken = () => localStorage.getItem('token');

const headers = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const getStoredUserType = () => {
  const directUserType = localStorage.getItem('userType');
  if (directUserType) return directUserType;
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return storedUser.userType || storedUser.type || null;
  } catch {
    return null;
  }
};

const getSubscriptionTitle = (status) => {
  switch (status) {
    case 'inactive': return 'Subscription Inactive';
    case 'cancelled': return 'Subscription Cancelled';
    case 'expired': return 'Subscription Expired';
    default: return 'Subscription Required';
  }
};

const getCompanyCurrency = (company) => {
  if (!company) return 'USD';
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === 'object' && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === 'string' && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === 'string' && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === 'object' && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === 'string' && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === 'object' && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) {
    currency = 'USD';
  }
  return currency;
};

const normalizeSubscriptionError = (data = {}, companyCurrency = 'USD') => {
  const subscription = data.subscription || {};
  const company = data.company || subscription.company || {};
  const status = subscription.status || data.status || 'expired';
  const companyName =
    company.name ||
    company.company_name ||
    data.companyName ||
    'Your company';

  return {
    ...data,
    success: false,
    subscriptionExpired: true,
    title: data.title || getSubscriptionTitle(status),
    message:
      data.message ||
      `${companyName} does not have an active subscription.`,
    details:
      data.details ||
      (status === 'expired'
        ? `The ${subscription.planName || 'IMS'} subscription has expired.`
        : `The subscription status is ${status}.`),
    amountDue: data.amountDue ?? subscription.price ?? 0,
    planName: data.planName || subscription.planName || '-',
    expiryDate: data.expiryDate || subscription.endDate || null,
    status,
    instructions:
      data.instructions ||
      'Access to HantiZone IMS is temporarily blocked. Please contact the HantiZone Accounts Department to renew or reactivate the subscription.',
    contact: {
      company: data.contact?.company || 'HantiZone',
      department: data.contact?.department || 'Accounts Department',
      phone: data.contact?.phone || '+252 61 0000000',
      email: data.contact?.email || 'accounts@hantizone.com',
    },
    company: {
      id: company.id || company._id || null,
      name: companyName,
      code: company.code || company.company_code || null,
    },
    subscription,
    currency_code: companyCurrency,
  };
};

const HomePage = () => {
  const { company } = useAuth();
  const { formatCurrency: contextFormatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);

  const [profitLoss, setProfitLoss] = useState({
    revenue: 0,
    cogs: 0,
    grossProfit: 0,
    operatingExpenses: 0,
    netProfitLoss: 0,
    loaded: false,
  });

  const [stats, setStats] = useState({
    sales: { total: 0, count: 0 },
    purchases: { total: 0, count: 0 },
    expenses: { total: 0, count: 0 },
    netProfit: 0,
    customers: 0,
    suppliers: 0,
    totalProducts: 0,
    lowStockCount: 0,
    totalStockValue: 0,
  });

  const [monthlyData, setMonthlyData] = useState({
    sales: Array(12).fill(0),
    purchases: Array(12).fill(0),
  });

  const [lowStockItems, setLowStockItems] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const getCurrentYear = () => new Date().getFullYear();

  const getCurrentMonthStart = () => {
    const date = new Date();
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getCurrentMonthEnd = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    date.setHours(23, 59, 59, 999);
    return date;
  };

  const formatNumber = (value) =>
    Number(value || 0).toLocaleString();

  const throwIfSubscriptionBlocked = (requestError) => {
    const responseData = requestError?.response?.data;
    if (responseData?.subscriptionExpired) {
      throw requestError;
    }
  };

  const checkCompanySubscription = useCallback(async () => {
    const userType = getStoredUserType();

    if (userType === 'system') {
      setSubscriptionError(null);
      return true;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/subscriptions/my-subscription`,
        { headers: headers() },
      );

      const responseData = response.data || {};

      if (responseData.allowed === false) {
        setSubscriptionError(
          normalizeSubscriptionError(responseData, companyCurrency),
        );
        return false;
      }

      setSubscriptionError(null);
      return true;
    } catch (requestError) {
      const responseData = requestError.response?.data;

      if (
        responseData?.subscriptionExpired ||
        requestError.response?.status === 403
      ) {
        setSubscriptionError(
          normalizeSubscriptionError(responseData, companyCurrency),
        );
        return false;
      }

      throw requestError;
    }
  }, [companyCurrency]);

  // ✅ FIX: Calculate P&L locally using invoices to match DailySummary exactly
  const fetchProfitLoss = useCallback(async () => {
    try {
      const currentMonthStart = getCurrentMonthStart();
      const currentMonthEnd = getCurrentMonthEnd();

      const invRes = await axios.get(`${API_BASE_URL}/api/sales/invoices`, {
        headers: headers(),
        params: {
          start_date: currentMonthStart.toISOString(),
          end_date: currentMonthEnd.toISOString(),
          limit: 5000,
        },
      });
      const invoices = invRes.data.data || [];

      let revenue = 0;
      let cogs = 0;

      invoices.forEach((inv) => {
        const sign = inv.is_return ? -1 : 1;
        const subTotal = Number(inv.sub_total || 0);
        const discount = Number(inv.total_discount || 0);
        revenue += sign * (subTotal - discount);

        const invCogs = (inv.sales_transactions || []).reduce(
          (sum, line) => sum + Number(line.inventory_total_cost || 0),
          0
        );
        cogs += sign * invCogs;
      });

      const expRes = await axios.get(`${API_BASE_URL}/api/expense`, {
        headers: headers(),
        params: {
          start_date: currentMonthStart.toISOString(),
          end_date: currentMonthEnd.toISOString(),
          limit: 5000,
        },
      });
      const expenses = expRes.data.data || [];
      const operatingExpenses = expenses.reduce(
        (sum, exp) => sum + Number(exp.amount || 0),
        0
      );

      const grossProfit = revenue - cogs;
      const netProfitLoss = grossProfit - operatingExpenses;

      setProfitLoss({
        revenue,
        cogs,
        grossProfit,
        operatingExpenses,
        netProfitLoss,
        loaded: true,
      });
    } catch (requestError) {
      throwIfSubscriptionBlocked(requestError);
      setProfitLoss((prev) => ({ ...prev, loaded: true }));
    }
  }, []);

  const fetchDashboardData = useCallback(
    async (isManualRefresh = false) => {
      if (isManualRefresh) setRefreshing(true);

      setError(null);

      try {
        const token = getToken();
        if (!token) throw new Error('No authentication token');

        const subscriptionAllowed = await checkCompanySubscription();
        if (!subscriptionAllowed) return;

        const currentYear = getCurrentYear();
        const yearStart = new Date(currentYear, 0, 1);
        const yearEnd = new Date(currentYear, 11, 31, 23, 59, 59, 999);
        const currentMonthStart = getCurrentMonthStart();
        const currentMonthEnd = getCurrentMonthEnd();

        // ✅ Fetch P&L in parallel with other data
        const profitLossPromise = fetchProfitLoss();

        // Sales
        const salesResponse = await axios.get(
          `${API_BASE_URL}/api/sales/invoices`,
          {
            headers: headers(),
            params: {
              start_date: yearStart.toISOString(),
              end_date: yearEnd.toISOString(),
              limit: 5000,
            },
          },
        );

        const allSales = salesResponse.data.data || [];
        const monthSales = allSales.filter((invoice) => {
          const invoiceDate = new Date(invoice.invoice_date);
          return invoiceDate >= currentMonthStart && invoiceDate <= currentMonthEnd;
        });

        const totalSalesMonth = monthSales.reduce(
          (sum, invoice) => {
            const sign = invoice.is_return ? -1 : 1;
            const net = Number(invoice.sub_total || 0) - Number(invoice.total_discount || 0);
            return sum + (sign * net);
          },
          0,
        );

        const monthlySales = Array(12).fill(0);
        allSales.forEach((invoice) => {
          const month = new Date(invoice.invoice_date).getMonth();
          if (month >= 0 && month <= 11) {
            const sign = invoice.is_return ? -1 : 1;
            const net = Number(invoice.sub_total || 0) - Number(invoice.total_discount || 0);
            monthlySales[month] += sign * net;
          }
        });

        // Purchases
        const purchaseResponse = await axios.get(
          `${API_BASE_URL}/api/purchase/invoices`,
          {
            headers: headers(),
            params: {
              start_date: yearStart.toISOString(),
              end_date: yearEnd.toISOString(),
              limit: 5000,
            },
          },
        );

        const allPurchases = purchaseResponse.data.data || [];
        const monthPurchases = allPurchases.filter((invoice) => {
          const invoiceDate = new Date(invoice.invoice_date);
          return invoiceDate >= currentMonthStart && invoiceDate <= currentMonthEnd;
        });

        const totalPurchasesMonth = monthPurchases.reduce(
          (sum, invoice) => sum + Number(invoice.grand_total || 0),
          0,
        );

        const monthlyPurchases = Array(12).fill(0);
        allPurchases.forEach((invoice) => {
          const month = new Date(invoice.invoice_date).getMonth();
          if (month >= 0 && month <= 11) {
            monthlyPurchases[month] += Number(invoice.grand_total || 0);
          }
        });

        // Expenses
        let totalExpensesMonth = 0;
        let expenseCount = 0;

        try {
          const expenseResponse = await axios.get(
            `${API_BASE_URL}/api/expense`,
            {
              headers: headers(),
              params: {
                startDate: `${currentYear}-01-01`,
                endDate: `${currentYear}-12-31`,
                limit: 5000,
              },
            },
          );

          const allExpenses = expenseResponse.data.data || [];
          const monthExpenses = allExpenses.filter((expense) => {
            const expenseDate = new Date(expense.expense_date);
            return expenseDate >= currentMonthStart && expenseDate <= currentMonthEnd;
          });

          totalExpensesMonth = monthExpenses.reduce(
            (sum, expense) => sum + Number(expense.amount || 0),
            0,
          );
          expenseCount = monthExpenses.length;
        } catch (requestError) {
          throwIfSubscriptionBlocked(requestError);
        }

        // Customers
        let customerCount = 0;
        try {
          const customerResponse = await axios.get(
            `${API_BASE_URL}/api/customer`,
            {
              headers: headers(),
              params: { status: 'active', limit: 1 },
            },
          );
          customerCount =
            customerResponse.data.total ||
            customerResponse.data.data?.length ||
            0;
        } catch (requestError) {
          throwIfSubscriptionBlocked(requestError);
        }

        // Suppliers
        let supplierCount = 0;
        try {
          const supplierResponse = await axios.get(
            `${API_BASE_URL}/api/supplier`,
            {
              headers: headers(),
              params: { status: 'active', limit: 1 },
            },
          );
          supplierCount =
            supplierResponse.data.total ||
            supplierResponse.data.data?.length ||
            0;
        } catch (requestError) {
          throwIfSubscriptionBlocked(requestError);
        }

        // Products and inventory
        let totalProducts = 0;
        let lowStockCount = 0;
        let totalStockValue = 0;
        let lowStockList = [];

        try {
          const productResponse = await axios.get(
            `${API_BASE_URL}/api/products`,
            {
              headers: headers(),
              params: { status: 'active', limit: 5000 },
            },
          );

          const allProducts =
            productResponse.data.data ||
            productResponse.data.items ||
            productResponse.data.results ||
            [];

          totalProducts = productResponse.data.total || allProducts.length || 0;

          const productReorderMap = {};
          const productNameMap = {};

          allProducts.forEach((p) => {
            const id = String(p._id || p.id || '');
            if (!id) return;

            const reorder = Number(
              p.reorder_level ??
                p.reorder_qty ??
                p.reorder_point ??
                p.min_qty ??
                p.min_stock_level ??
                p.minimum_stock ??
                0,
            );

            productReorderMap[id] = reorder;
            productNameMap[id] = p.product_name || 'Unknown';
          });

          const stockResponse = await axios.get(
            `${API_BASE_URL}/api/inventorystock`,
            {
              headers: headers(),
              params: { status: 'active', limit: 5000 },
            },
          );

          const stocks = stockResponse.data.data || [];

          totalStockValue = stocks.reduce(
            (sum, stock) =>
              sum + Number(stock.quantity || 0) * Number(stock.avg_cost || 0),
            0,
          );

          const computeEffectiveMin = (stock) => {
            const stockMin = Number(stock.min_qty || 0);
            if (stockMin > 0) return stockMin;

            const productId = String(stock.product_id?._id || stock.product_id || '');
            return Number(productReorderMap[productId] || 0);
          };

          const isLowStock = (stock) => {
            const available = Number(stock.available_qty ?? stock.quantity ?? 0);
            const min = computeEffectiveMin(stock);
            return min > 0 && available < min;
          };

          lowStockCount = stocks.filter(isLowStock).length;

          const sortedLowStock = stocks
            .filter(isLowStock)
            .sort((a, b) => {
              const aAvail = Number(a.available_qty ?? a.quantity ?? 0);
              const bAvail = Number(b.available_qty ?? b.quantity ?? 0);
              const aMin = computeEffectiveMin(a);
              const bMin = computeEffectiveMin(b);
              return bMin - bAvail - (aMin - aAvail);
            })
            .slice(0, 5);

          lowStockList = sortedLowStock.map((stock) => {
            const productId = String(stock.product_id?._id || stock.product_id || '');
            const productName =
              stock.product_id?.product_name ||
              productNameMap[productId] ||
              'Unknown';

            return {
              product_name: productName,
              current_qty: stock.available_qty ?? stock.quantity ?? 0,
              min_qty: computeEffectiveMin(stock),
            };
          });
        } catch (requestError) {
          throwIfSubscriptionBlocked(requestError);
        }

        await profitLossPromise;

        setStats({
          sales: { total: totalSalesMonth, count: monthSales.length },
          purchases: { total: totalPurchasesMonth, count: monthPurchases.length },
          expenses: { total: totalExpensesMonth, count: expenseCount },
          netProfit: totalSalesMonth - totalPurchasesMonth - totalExpensesMonth,
          customers: customerCount,
          suppliers: supplierCount,
          totalProducts,
          lowStockCount,
          totalStockValue,
        });

        setMonthlyData({
          sales: monthlySales,
          purchases: monthlyPurchases,
        });

        setLowStockItems(lowStockList);
        setLastUpdated(new Date());
        setSubscriptionError(null);
      } catch (requestError) {
        const responseData = requestError.response?.data;

        if (responseData?.subscriptionExpired) {
          setSubscriptionError(
            normalizeSubscriptionError(responseData, companyCurrency),
          );
          setError(null);
        } else {
          setError(
            responseData || {
              message: requestError.message || 'Failed to load dashboard',
            },
          );
        }
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    [checkCompanySubscription, companyCurrency, fetchProfitLoss],
  );

  useEffect(() => {
    fetchDashboardData();

    const interval = setInterval(() => {
      fetchDashboardData(false);
    }, 300000);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const barChartData = useMemo(
    () => ({
      labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
      datasets: [
        {
          label: 'Sales',
          data: monthlyData.sales,
          backgroundColor: '#f99933',
          borderRadius: 6,
        },
        {
          label: 'Purchases',
          data: monthlyData.purchases,
          backgroundColor: '#170f48',
          borderRadius: 6,
        },
      ],
    }),
    [monthlyData],
  );

  const lineChartData = useMemo(
    () => ({
      labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
      datasets: [
        {
          label: 'Sales',
          data: monthlyData.sales,
          borderColor: '#f99933',
          backgroundColor: 'rgba(249,153,51,0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Purchases',
          data: monthlyData.purchases,
          borderColor: '#170f48',
          backgroundColor: 'rgba(23,15,72,0.05)',
          fill: true,
          tension: 0.4,
        },
      ],
    }),
    [monthlyData],
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${formatCurrency(context.raw)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (value) => formatCurrency(value) },
      },
    },
  };

  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('company');
    window.location.href = '/login';
  };

  if (initialLoading) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#170f48',
          fontWeight: 600,
        }}
      >
        Loading dashboard data...
      </div>
    );
  }

  if (subscriptionError?.subscriptionExpired) {
    const amountDue = Number(subscriptionError.amountDue || 0).toFixed(2);
    const expiryDate = subscriptionError.expiryDate
      ? new Date(subscriptionError.expiryDate).toLocaleDateString()
      : 'Not available';
    const subscriptionStatus =
      subscriptionError.status ||
      subscriptionError.subscription?.status ||
      'inactive';
    const formattedAmountDue = formatCurrency(amountDue);

    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fb 0%, #fff8f2 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '28px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '760px',
            background: '#ffffff',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 18px 55px rgba(23, 15, 72, 0.16)',
          }}
        >
          <div style={{ height: '7px', background: 'linear-gradient(90deg, #f1592a, #f99933)' }} />

          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div
              style={{
                width: '82px',
                height: '82px',
                margin: '0 auto 22px',
                borderRadius: '50%',
                background: 'rgba(241, 89, 42, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '38px',
              }}
            >
              ⚠️
            </div>

            <p
              style={{
                display: 'inline-block',
                margin: '0 0 12px',
                padding: '7px 14px',
                borderRadius: '999px',
                background: '#fff1e8',
                color: '#d65322',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '12px',
                letterSpacing: '0.8px',
              }}
            >
              {subscriptionStatus}
            </p>

            <h1 style={{ color: '#170f48', margin: '0 0 14px', fontSize: '34px' }}>
              {subscriptionError.title || 'Subscription Expired'}
            </h1>

            <p
              style={{
                color: '#50505f',
                fontSize: '17px',
                lineHeight: 1.7,
                margin: '0 auto',
                maxWidth: '610px',
              }}
            >
              {subscriptionError.message}
            </p>

            {subscriptionError.details && (
              <p style={{ color: '#777785', lineHeight: 1.65, marginTop: '10px' }}>
                {subscriptionError.details}
              </p>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '14px',
                margin: '30px 0',
              }}
            >
              <div style={{ background: '#f8f9fc', border: '1px solid #ececf3', padding: '20px', borderRadius: '12px' }}>
                <small style={{ color: '#777785' }}>Plan</small>
                <h3 style={{ color: '#170f48', margin: '8px 0 0' }}>
                  {subscriptionError.planName || '-'}
                </h3>
              </div>

              <div style={{ background: '#fff8f3', border: '1px solid #ffe2cf', padding: '20px', borderRadius: '12px' }}>
                <small style={{ color: '#777785' }}>Amount Due</small>
                <h3 style={{ color: '#f1592a', margin: '8px 0 0' }}>
                  {formattedAmountDue}
                </h3>
              </div>

              <div style={{ background: '#f8f9fc', border: '1px solid #ececf3', padding: '20px', borderRadius: '12px' }}>
                <small style={{ color: '#777785' }}>Expiry Date</small>
                <h3 style={{ color: '#170f48', margin: '8px 0 0' }}>
                  {expiryDate}
                </h3>
              </div>
            </div>

            <p style={{ color: '#555565', lineHeight: 1.7 }}>
              {subscriptionError.instructions}
            </p>

            <div
              style={{
                marginTop: '24px',
                background: 'rgba(23, 15, 72, 0.05)',
                borderRadius: '14px',
                padding: '22px',
                textAlign: 'left',
              }}
            >
              <h3 style={{ color: '#170f48', margin: '0 0 14px' }}>
                Contact HantiZone Accounts
              </h3>
              <p style={{ margin: '8px 0' }}>
                <strong>Company:</strong> {subscriptionError.company?.name || '-'}
              </p>
              <p style={{ margin: '8px 0' }}>
                <strong>Department:</strong> {subscriptionError.contact?.department || 'Accounts Department'}
              </p>
              <p style={{ margin: '8px 0' }}>
                <strong>Phone:</strong> {subscriptionError.contact?.phone || '-'}
              </p>
              <p style={{ margin: '8px 0' }}>
                <strong>Email:</strong> {subscriptionError.contact?.email || '-'}
              </p>
            </div>

            <div
              style={{
                marginTop: '28px',
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <button
                type="button"
                onClick={() => navigate('/MySubscription')}
                style={{
                  background: '#170f48',
                  color: '#ffffff',
                  border: 'none',
                  padding: '13px 24px',
                  borderRadius: '9px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FiRefreshCw />
                Check Subscription Again
              </button>

              <button
                type="button"
                onClick={handleLogout}
                style={{
                  background: '#ffffff',
                  color: '#170f48',
                  border: '1px solid #d8d8e3',
                  padding: '13px 24px',
                  borderRadius: '9px',
                  cursor: 'pointer',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FiLogOut /> Log Out
              </button>
            </div>
          </div>
        </div>

        <style>{`
          .spinning { animation: spin 1s linear infinite; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', background: '#f8f9fa', minHeight: '100vh' }}>
        <div
          style={{
            background: '#fff3f3',
            borderLeft: '4px solid #dc3545',
            padding: '16px',
            borderRadius: '8px',
            color: '#dc3545',
          }}
        >
          {error.message || 'Failed to load dashboard'}
        </div>
      </div>
    );
  }

  const styles = {
    container: {
      padding: '24px',
      background: '#f8f9fa',
      minHeight: '100vh',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
    },
    title: {
      fontSize: '1.8rem',
      color: '#170f48',
      margin: 0,
    },
    refreshBtn: {
      background: '#170f48',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '20px',
      marginBottom: '32px',
    },
    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      borderLeft: '4px solid #f99933',
      transition: '0.2s',
    },
    statIcon: {
      fontSize: '1.8rem',
      color: '#f99933',
      marginBottom: '8px',
    },
    statLabel: {
      margin: 0,
      color: '#6c757d',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
    },
    statValue: {
      margin: '8px 0 4px',
      fontSize: '1.8rem',
      fontWeight: 'bold',
      color: '#170f48',
    },
    statSmall: {
      color: '#adb5bd',
      fontSize: '0.7rem',
    },
    chartsSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
      gap: '24px',
      marginBottom: '32px',
    },
    chartContainer: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      minHeight: '400px',
    },
    chartTitle: {
      margin: '0 0 16px 0',
      color: '#170f48',
      fontSize: '1.2rem',
    },
    lowstockSection: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '24px',
    },
    lowstockTitle: {
      margin: '0 0 16px',
      color: '#170f48',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      padding: '10px',
      textAlign: 'left',
      borderBottom: '1px solid #e9ecef',
      background: '#f8f9fa',
      color: '#170f48',
    },
    td: {
      padding: '10px',
      textAlign: 'left',
      borderBottom: '1px solid #e9ecef',
    },
    statusLow: {
      color: '#dc3545',
      fontWeight: 'bold',
    },
    lastUpdated: {
      textAlign: 'right',
      fontSize: '0.7rem',
      color: '#adb5bd',
      marginTop: '16px',
    },
  };

  const isProfit = profitLoss.netProfitLoss >= 0;
  const plColor = isProfit ? '#28a745' : '#dc3545';
  const plBg = isProfit ? '#f0fdf4' : '#fef2f2';
  const plBorder = isProfit ? '#bbf7d0' : '#fecaca';

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>HantiZone IMS Dashboard</h1>

        <button
          style={styles.refreshBtn}
          onClick={handleManualRefresh}
          disabled={refreshing}
        >
          <FiRefreshCw className={refreshing ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* ============================================================
          ✅ PROFIT / LOSS BANNER — real accounting P&L
      ============================================================ */}
      {profitLoss.loaded && (
        <div
          style={{
            background: plBg,
            border: `1px solid ${plBorder}`,
            borderRadius: 16,
            padding: '24px 28px',
            marginBottom: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: plColor,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                flexShrink: 0,
              }}
            >
              {isProfit ? <FiTrendingUp /> : <FiTrendingDown />}
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: plColor,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                {isProfit ? 'Net Profit · This Period' : 'Net Loss · This Period'}
              </div>
              <div
                style={{
                  fontSize: 36,
                  fontWeight: 800,
                  color: plColor,
                  lineHeight: 1.1,
                }}
              >
                {formatCurrency(Math.abs(profitLoss.netProfitLoss))}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: '#475569',
                  marginTop: 6,
                }}
              >
                {isProfit
                  ? 'Revenue exceeds all costs and expenses.'
                  : 'Costs and expenses exceed revenue.'}
              </div>
            </div>
          </div>

          {/* P&L breakdown */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(110px, 1fr))',
              gap: 16,
              minWidth: 360,
            }}
          >
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                Net Revenue
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                {formatCurrency(profitLoss.revenue)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                COGS
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                {formatCurrency(profitLoss.cogs)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                Operating Exp.
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                {formatCurrency(profitLoss.operatingExpenses)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiDollarSign /></div>
          <p style={styles.statLabel}>Sales (this month)</p>
          <h3 style={styles.statValue}>{formatCurrency(stats.sales.total)}</h3>
          <small style={styles.statSmall}>{stats.sales.count} invoices</small>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiShoppingCart /></div>
          <p style={styles.statLabel}>Purchases (this month)</p>
          <h3 style={styles.statValue}>{formatCurrency(stats.purchases.total)}</h3>
          <small style={styles.statSmall}>{stats.purchases.count} invoices</small>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiFileText /></div>
          <p style={styles.statLabel}>Expenses (this month)</p>
          <h3 style={styles.statValue}>{formatCurrency(stats.expenses.total)}</h3>
          <small style={styles.statSmall}>{stats.expenses.count} transactions</small>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}>
            {profitLoss.netProfitLoss >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
          </div>
          <p style={styles.statLabel}>
            {profitLoss.netProfitLoss >= 0 ? 'Net Profit' : 'Net Loss'} (period)
          </p>
          <h3
            style={{
              ...styles.statValue,
              color: profitLoss.netProfitLoss >= 0 ? '#28a745' : '#dc3545',
            }}
          >
            {formatCurrency(Math.abs(profitLoss.netProfitLoss))}
          </h3>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiUsers /></div>
          <p style={styles.statLabel}>Total Customers</p>
          <h3 style={styles.statValue}>{formatNumber(stats.customers)}</h3>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiUsers /></div>
          <p style={styles.statLabel}>Total Suppliers</p>
          <h3 style={styles.statValue}>{formatNumber(stats.suppliers)}</h3>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiPackage /></div>
          <p style={styles.statLabel}>Total Products</p>
          <h3 style={styles.statValue}>{formatNumber(stats.totalProducts)}</h3>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiAlertCircle /></div>
          <p style={styles.statLabel}>Low Stock Items</p>
          <h3
            style={{
              ...styles.statValue,
              color: stats.lowStockCount > 0 ? '#dc3545' : '#28a745',
            }}
          >
            {formatNumber(stats.lowStockCount)}
          </h3>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statIcon}><FiDollarSign /></div>
          <p style={styles.statLabel}>Inventory Value</p>
          <h3 style={styles.statValue}>{formatCurrency(stats.totalStockValue)}</h3>
        </div>
      </div>

      <div style={styles.chartsSection}>
        <div style={styles.chartContainer}>
          <h3 style={styles.chartTitle}>Monthly Sales vs Purchases (Bar)</h3>
          <div style={{ height: '350px' }}>
            <Bar data={barChartData} options={chartOptions} />
          </div>
        </div>

        <div style={styles.chartContainer}>
          <h3 style={styles.chartTitle}>Monthly Sales vs Purchases (Line)</h3>
          <div style={{ height: '350px' }}>
            <Line data={lineChartData} options={chartOptions} />
          </div>
        </div>
      </div>

      {lowStockItems.length > 0 && (
        <div style={styles.lowstockSection}>
          <h3 style={styles.lowstockTitle}>Top Low Stock Items</h3>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Product</th>
                <th style={styles.th}>Current Qty</th>
                <th style={styles.th}>Min Qty</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>

            <tbody>
              {lowStockItems.map((item, index) => (
                <tr key={`${item.product_name}-${index}`}>
                  <td style={styles.td}>{item.product_name}</td>
                  <td style={styles.td}>{item.current_qty}</td>
                  <td style={styles.td}>{item.min_qty}</td>
                  <td style={{ ...styles.td, ...styles.statusLow }}>Low Stock</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastUpdated && (
        <div style={styles.lastUpdated}>
          Last updated: {lastUpdated.toLocaleString()}
        </div>
      )}

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default HomePage;
